import { useRef, useState } from 'react';
import { Link, useLocation } from '@remix-run/react';
import type {
    TileableModel,
    TileViewModel,
} from '@rocco/ui/tile-shared/view-models/tile';
import { Grid } from '@rocco/ui/layout/components/Grid';
import { pxToRem } from '@archipro-design/aria';
import { Button } from '@rocco/components/button';
import { CategoryPageTileWithAdminActions } from '../category-page/CategoryPageTileWithAdminActions';
import { useCategoryTilesTracking } from '@ui-rocco/hooks/use-category-tiles-tracking';
import { cn } from '@rocco/utils/cn';
import { DetailsPageProductTileGrid } from './DetailsPageProductTileGrid';
import { DetailsPageProfessionalTileGrid } from './DetailsPageProfessionalTileGrid';
import { createSectionScrollOnCollapse } from '../../utils/use-section-scroll-on-collapse';

type ButtonProps = React.ComponentProps<typeof Button>;
type CTAProps =
    | ({
          type: 'link';
          label: string;
          loading?: boolean;
          onClick?: () => void;
          color?: ButtonProps['color'];
      } & React.ComponentProps<typeof Link>)
    | ({
          type: 'button';
          onClick: () => void;
          label: string;
          loading?: boolean;
          color?: ButtonProps['color'];
      } & React.ComponentProps<typeof Button>)
    | { type: 'showAll' };

/**
 * Props interface for the DetailsPageRelatedTileGrid component
 * @interface DetailsPageRelatedTileGridProps
 * @property {React.ReactNode} title - Title for the related items section
 * @property {TileViewModel<TileableModel>[]} tiles - Array of related tiles to display
 * @property {CTAProps} cta - Call to action configuration (either link or button)
 */
interface DetailsPageRelatedTileGridProps {
    title: React.ReactNode;
    titleClassName?: string;
    tiles: TileViewModel<TileableModel>[];
    showTitleWhenEmpty?: boolean;
    isLoading?: boolean;
    cta?: CTAProps;
    type: 'products' | 'projects' | 'articles' | 'professionals';
    displayCount?: {
        desktop: number;
        mobile: number;
    };
    columns?: {
        desktop: number;
        mobile: number;
    };
    appDisplayMode: 'mobile' | 'desktop';
    noSlice?: boolean;
    section?: string;
    hideBorder?: boolean;
    /** Show a border line under the section title on desktop */
    showTitleBorder?: boolean;
}

/**
 * Component that renders a grid of related tiles with a title and CTA
 * Displays tiles in a responsive grid layout with a call-to-action button or link
 * @param {DetailsPageRelatedTileGridProps} props - Component props
 * @returns {JSX.Element} The rendered grid of related tiles
 */
export const DetailsPageRelatedTileGrid = (
    props: DetailsPageRelatedTileGridProps,
) => {
    const {
        title,
        titleClassName,
        tiles,
        showTitleWhenEmpty = false,
        isLoading = false,
        cta,
        displayCount = { desktop: 8, mobile: 4 },
        appDisplayMode,
        columns,
        noSlice = false,
        section,
    } = props;
    const gridRef = useRef<HTMLDivElement | null>(null);
    const sectionRef = useRef<HTMLDivElement>(null);
    const [showAll, setShowAll] = useState(false);

    useCategoryTilesTracking(gridRef);

    const count = displayCount[appDisplayMode];

    const location = useLocation();
    const isProjectPage = location.pathname.includes('/project/');

    const tilesToDisplay = showAll || noSlice ? tiles : tiles.slice(0, count);
    const tileDoms = tilesToDisplay.map((tile, index) => {
        const forceHalf =
            props.type === 'products' && appDisplayMode === 'desktop';
        const wrapperStyle =
            forceHalf && isProjectPage
                ? { flex: '0 0 auto', width: pxToRem(437) }
                : undefined;
        const tileWithAspect =
            forceHalf && isProjectPage
                ? { ...tile, aspectRatio: 'product' as const }
                : tile;

        return (
            <div
                key={`related_tile_${tile.model.id}_${index}`}
                style={wrapperStyle}
            >
                {section ? (
                    <CategoryPageTileWithAdminActions
                        tile={{
                            ...tileWithAspect,
                            section: section,
                        }}
                    />
                ) : (
                    <CategoryPageTileWithAdminActions tile={tileWithAspect} />
                )}
            </div>
        );
    });
    const skeletonTileDoms = Array.from({ length: count }, (_, index) => {
        return (
            <div
                key={`related_tile_skeleton_${index}`}
                aria-hidden="true"
                className="space-y-3"
            >
                <div className="bg-black/6 aspect-square animate-pulse rounded-[0.625rem]" />
                <div className="space-y-2">
                    <div className="bg-black/8 h-3 w-4/5 animate-pulse rounded" />
                    <div className="bg-black/8 h-3 w-2/3 animate-pulse rounded" />
                </div>
            </div>
        );
    });
    const gridDoms =
        isLoading && tileDoms.length === 0 ? skeletonTileDoms : tileDoms;

    const gridColumns = getGridColumns(props.type, columns);
    const titleClassNames = cn(
        'pt-5 text-5 font-medium tracking-[0.025rem]',
        titleClassName,
    );

    const handleShowAllToggle = createSectionScrollOnCollapse(
        showAll,
        setShowAll,
        sectionRef,
        {
            onlyOnDesktop: true,
            appDisplayMode,
        },
    );

    if (tiles.length === 0 && !showTitleWhenEmpty && !isLoading) {
        return null;
    }

    if (
        props.type === 'products' &&
        appDisplayMode === 'desktop' &&
        isProjectPage
    ) {
        return <DetailsPageProductTileGrid title={title} tileDoms={tileDoms} />;
    }

    // Special desktop layout for professionals: title on the left, list of names on the right
    if (props.type === 'professionals' && appDisplayMode === 'desktop') {
        return (
            <DetailsPageProfessionalTileGrid
                title={title}
                titleClassName={titleClassName}
                tiles={tilesToDisplay}
                showTitleBorder={props.showTitleBorder}
            />
        );
    }

    return (
        <div ref={sectionRef}>
            {title && (
                <div className="pb-8">
                    <h2 className={titleClassNames}>{title}</h2>
                    {props.showTitleBorder && appDisplayMode === 'desktop' && (
                        <div className="relative w-full border-t border-[#141414]/20" />
                    )}
                </div>
            )}

            {gridDoms.length > 0 && (
                <>
                    <Grid
                        colClasses={gridColumns}
                        rowsGapClass="gap-x-2 gap-y-12 md:gap-x-6 md:gap-y-20"
                        ref={gridRef}
                    >
                        {gridDoms}
                    </Grid>

                    <div className="flex justify-center pt-12">
                        {!isLoading && cta ? (
                            cta.type === 'link' ? (
                                <Button
                                    color={cta.color || 'secondary'}
                                    weight="default"
                                    asChild
                                    isLoading={cta.loading}
                                    onClick={cta.onClick}
                                >
                                    <Link {...cta}>{cta.label}</Link>
                                </Button>
                            ) : cta.type === 'button' ? (
                                <Button
                                    color={cta.color || 'secondary'}
                                    weight="default"
                                    onClick={cta.onClick}
                                    isLoading={cta.loading}
                                >
                                    {cta.label}
                                </Button>
                            ) : (
                                <Button
                                    color="secondary"
                                    weight="default"
                                    onClick={handleShowAllToggle}
                                >
                                    {showAll ? 'View Less' : 'View All'}
                                </Button>
                            )
                        ) : (
                            <Button
                                className="invisible"
                                color="secondary"
                                weight="default"
                                aria-hidden="true"
                                tabIndex={-1}
                            >
                                &nbsp;
                            </Button>
                        )}
                    </div>
                </>
            )}
        </div>
    );
};

const getGridColumns = (
    type: DetailsPageRelatedTileGridProps['type'],
    columnsOverride?: DetailsPageRelatedTileGridProps['columns'],
) => {
    if (columnsOverride) {
        return `grid-cols-${columnsOverride.mobile} md:grid-cols-${columnsOverride.desktop}`;
    }

    switch (type) {
        case 'products':
            return 'grid-cols-2 md:grid-cols-5';
        case 'projects':
            return 'grid-cols-1 md:grid-cols-4';
        case 'articles':
            return 'grid-cols-1 md:grid-cols-4';
        case 'professionals':
            return 'grid-cols-1 md:grid-cols-4';
    }
};
